import React, { createContext, useContext, useState, useCallback, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import backend from '../backend';
import { Items, LookupTree, SquiggleItem, SavingItem } from '../types/types';
import { makeLookupTreeFromItems } from '../biz/linkFinder';
import { itemSortingFunction, itemStateSortingFunction, itemStateReverseChronologicalSortingFunction } from '../biz/itemUtil';
import { systemItems } from '../biz/systemItems';
import { unstable_batchedUpdates } from 'react-dom';

interface AdventureDataContextProps {
  adventureLoaded: boolean;
  adventureName: string;
  adventureId: string;
  quests: SquiggleItem[];
  peopleAndOrgs: SquiggleItem[];
  places: SquiggleItem[];
  misc: SquiggleItem[];
  logEntries: SquiggleItem[];
  allUserItems: SquiggleItem[];
  allItems: SquiggleItem[];
  itemById: Map<string, SquiggleItem>;
  itemLookupTree: LookupTree;
  reloadItems: () => Promise<Items>;
  saveItem: (savedItem: SavingItem) => Promise<void>;
}

const AdventureDataContext = createContext<AdventureDataContextProps | undefined>(undefined);

export const AdventureDataProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const params = useParams();
  const adventureId = params.id!;
  const [adventureLoaded, setAdventureLoaded] = useState<boolean>(false);
  const [adventureName, setAdventureName] = useState<string>('');
  const [quests, setQuests] = useState<SquiggleItem[]>([]);
  const [peopleAndOrgs, setPeopleAndOrgs] = useState<SquiggleItem[]>([]);
  const [places, setPlaces] = useState<SquiggleItem[]>([]);
  const [misc, setMisc] = useState<SquiggleItem[]>([]);
  const [logEntries, setLogEntries] = useState<SquiggleItem[]>([]);
  const [itemLookupTree, setItemLookupTree] = useState<LookupTree>(new Map());

  const loadEverything = useCallback(async () => {
    if (!adventureId) {
      throw new Error('Adventure ID is not set');
    }
    const response = await backend.loadAdventure(adventureId);
    if (response.ok) {
      const adventure = await response.json();
      unstable_batchedUpdates(() => {
        setAdventureName(adventure.adventure.name);
        setQuests(adventure.quests.sort(itemStateSortingFunction));
        setPeopleAndOrgs(adventure.peopleAndOrgs.sort(itemSortingFunction));
        setPlaces(adventure.places.sort(itemSortingFunction));
        setMisc(adventure.misc.sort(itemSortingFunction));
        setLogEntries(adventure.logEntries.sort(itemStateReverseChronologicalSortingFunction));
        setItemLookupTree(makeLookupTreeFromItems([...adventure.quests, ...adventure.peopleAndOrgs, ...adventure.places, ...adventure.misc, ...adventure.logEntries, ...systemItems]));
        setAdventureLoaded(true);
      });
      return adventure;
    } else {
      throw new Error('Failed to load adventure data from backend');
    }
  }, [adventureId, setAdventureName, setQuests, setPeopleAndOrgs, setPlaces, setMisc, setLogEntries, setItemLookupTree, setAdventureLoaded]);

  const reloadItems = useCallback(async () => {
    if (!adventureId) {
      throw new Error('Adventure ID is not set');
    }
    const response = await backend.loadItems(adventureId);
    if (response.ok) {
      const items = await response.json();
      unstable_batchedUpdates(() => {
        setQuests(items.quests.sort(itemStateSortingFunction));
        setPeopleAndOrgs(items.peopleAndOrgs.sort(itemSortingFunction));
        setPlaces(items.places.sort(itemSortingFunction));
        setMisc(items.misc.sort(itemSortingFunction));
        setLogEntries(items.logEntries.sort(itemStateReverseChronologicalSortingFunction));
        setItemLookupTree(makeLookupTreeFromItems([...items.quests, ...items.peopleAndOrgs, ...items.places, ...items.misc, ...items.logEntries, ...systemItems]));
      });
      return items;
    } else {
      throw new Error('Failed to reload item data from backend');
    }
  }, [adventureId, setQuests, setPeopleAndOrgs, setPlaces, setMisc, setLogEntries, setItemLookupTree]);

  const saveItem = useCallback(async (savedItem: SavingItem) => {
    const existingItem = [...quests, ...peopleAndOrgs, ...places, ...misc, ...logEntries].find(item => item.id === savedItem.id);
    if (existingItem) {
      // Force a rerender of in-memory items while waiting for possible backend update
      if (existingItem.type === 'quest') {
        setQuests([...quests]);
      } else if (existingItem.type === 'personororg') {
        setPeopleAndOrgs([...peopleAndOrgs]);
      } else if (existingItem.type === 'place') {
        setPlaces([...places]);
      } else if (existingItem.type === 'miscitem') {
        setMisc([...misc]);
      } else if (existingItem.type === 'logentry') {
        setLogEntries([...logEntries]);
      }
    }

    const saveResponse = await backend.updateItem(adventureId, savedItem);
    if (!saveResponse.ok) {
      return;
    }
  }, [adventureId, quests, peopleAndOrgs, places, misc, logEntries, setQuests, setPeopleAndOrgs, setPlaces, setMisc, setLogEntries]);

  useEffect(() => {
    if (adventureId) {
        loadEverything();
    }
  }, [adventureId, loadEverything]);

  const allUserItems = useMemo(() =>
    [...quests, ...peopleAndOrgs, ...places, ...misc, ...logEntries],
    [quests, peopleAndOrgs, places, misc, logEntries]
  );

  const allItems = useMemo(() =>
    [...allUserItems, ...systemItems],
    [allUserItems]
  );

  const itemById = useMemo(() => {
    const map = new Map<string, SquiggleItem>();
    for (const candidateItem of allItems) {
      map.set(candidateItem.id, candidateItem);
    }
    return map;
  }, [allItems]);

  return (
    <AdventureDataContext.Provider value={{
        adventureId: adventureId,
        adventureLoaded,
        adventureName,
        quests,
        peopleAndOrgs,
        places,
        misc,
        logEntries,
        allUserItems,
        allItems,
        itemById,
        itemLookupTree,
        reloadItems,
        saveItem }}>
      {children}
    </AdventureDataContext.Provider>
  );
};

export const useAdventureDataContext = () => {
  const context = useContext(AdventureDataContext);
  if (!context) {
    throw new Error('useAdventureDataContext must be used within an AdventureDataProvider');
  }
  return context;
};
